import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { PetrolPump } from "./PetrolPumps";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";

interface AddPetrolPumpModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (petrolPump: any) => void;
  petrolPump?: PetrolPump;
  isEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  petrolPumpName: Yup.string()
    .required("Petrol pump name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  providerName: Yup.string()
    .required("Provider name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  primaryContactName: Yup.string()
    .required("Primary contact name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  primaryContactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]+$/, "Contact number must contain only digits")
    .min(10, "Contact number must be exactly 10 digits")
    .max(10, "Contact number must be exactly 10 digits"),
  location: Yup.string().max(255, "Location must not exceed 100 characters"),
});

const initialValues: Omit<PetrolPump, "id"> = {
  petrolPumpName: "",
  primaryContactName: "",
  primaryContactNumber: "",
  bulkDiscountPerLitre: 0,
  providerName: "",
  otherProviderName: "",
  location: "",
  companyId: "",
};

const petrolPumpProviders = [
  "Indian Oil Corporation Ltd (IOCL)",
  "Bharat Petroleum Corporation Ltd (BPCL)",
  "Hindustan Petroleum Corporation Ltd (HPCL)",
  "Reliance Industries Ltd (RIL)",
  "Nayara Energy (formerly Essar Oil)",
  "Shell India",
  "ONGC (for captive consumption)",
  "MRPL (Mangalore Refinery and Petrochemicals Limited)",
  "Jio-bp (BP-Reliance joint venture)",
  "TotalEnergies Marketing India Pvt Ltd",
  "Assam Oil Division (IOCL)",
  "Other",
];

const AddPetrolPumpModal: React.FC<AddPetrolPumpModalProps> = ({
  open,
  onClose,
  onSubmit,
  petrolPump,
  isEdit = false,
}) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const handleSubmit = (values: any) => {
    const enrichedValues = {
      ...values,
      ...(values.providerName === "Other" && {
        providerName: values.otherProviderName,
      }),
      ...(!isEdit && { companyId: companyId }),
    };
    onSubmit(enrichedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Update Petrol Pump" : "Add New Petrol Pump"}
      </DialogTitle>
      <Formik
        initialValues={petrolPump || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isValid, dirty, setFieldValue }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {isEdit && values?.providerName !== "" ? (
                    <Field
                      as={TextField}
                      label="Provider Name"
                      name="providerName"
                      fullWidth
                      value={values.providerName}
                      {...commonTextFieldProps}
                    />
                  ) : (
                    <TextField
                      select
                      label="Provider Name"
                      name="providerName"
                      fullWidth
                      required
                      value={values.providerName}
                      error={
                        touched.providerName && Boolean(errors.providerName)
                      }
                      helperText={touched.providerName && errors.providerName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("providerName", e.target.value);
                      }}
                      {...commonTextFieldProps}
                    >
                      <MenuItem
                        value="Select"
                        disabled
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Select
                      </MenuItem>
                      {petrolPumpProviders?.map((provider) => (
                        <MenuItem
                          key={provider}
                          value={provider}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {provider}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                {values.providerName === "Other" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Specify Provider Name"
                      value={values.otherProviderName}
                      name="otherProviderName"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("otherProviderName", e.target.value);
                      }}
                      error={
                        touched.otherProviderName &&
                        Boolean(errors.otherProviderName)
                      }
                      helperText={
                        touched.otherProviderName && errors.otherProviderName
                      }
                      required={values.providerName === "Other"}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Petrol Pump Name"
                    name="petrolPumpName"
                    value={values.petrolPumpName}
                    error={
                      touched.petrolPumpName && Boolean(errors.petrolPumpName)
                    }
                    helperText={touched.petrolPumpName && errors.petrolPumpName}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Primary Contact Name"
                    name="primaryContactName"
                    value={values.primaryContactName}
                    error={
                      touched.primaryContactName &&
                      Boolean(errors.primaryContactName)
                    }
                    helperText={
                      touched.primaryContactName && errors.primaryContactName
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Primary Contact Number"
                    name="primaryContactNumber"
                    // type="number"
                    value={values.primaryContactNumber}
                    error={
                      touched.primaryContactNumber &&
                      Boolean(errors.primaryContactNumber)
                    }
                    helperText={
                      touched.primaryContactNumber &&
                      errors.primaryContactNumber
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                    InputProps={{
                      inputMode: "numeric",
                    }}
                    placeholder="Enter 10 digit number"
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address"
                    name="location"
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    placeholder="petrol pump location"
                    value={values.location}
                    required
                    multiline
                    rows={2}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Bulk Discount Per Litre"
                    name="bulkDiscountPerLitre"
                    value={values.bulkDiscountPerLitre}
                    error={
                      touched.bulkDiscountPerLitre &&
                      Boolean(errors.bulkDiscountPerLitre)
                    }
                    helperText={
                      touched.bulkDiscountPerLitre &&
                      errors.bulkDiscountPerLitre
                    }
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
                      inputProps: {
                        style: { textAlign: "left" },
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                          (e.target as HTMLInputElement).blur(),
                      },
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      const formattedValue =
                        value === "0" ? "0" : value.replace(/^0+/, "");
                      setFieldValue("bulkDiscountPerLitre", formattedValue);
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddPetrolPumpModal;
